/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select } from '../Select/Select'
import { useServiceContext } from '../../root'
import { usersApiService } from '../../services/api/users'

export interface ApproverStep {
  id?: string // Unique identifier for React key
  processingType: string
  taskTeamStepLabel: string | number | null
  nofAllValue: number | null
  usersGroups: string[]
}

interface UserGroupStepsProps {
  steps: ApproverStep[]
  onChange: (steps: ApproverStep[]) => void
  labelTxt: string
  nofallTxt: string
  disabled?: boolean
}

interface UserGroupOption {
  value: string
  label: string
  id: number
  systemHelperType: string
}

interface StepLabelOption {
  value: number
  label: string
}

const PROCESSING_TYPES = {
  SERIAL: 'SERIAL',
  PARALLEL: 'PARALLEL',
  N_OF_ALL: 'N_OF_ALL'
} as const

/**
 * UserGroupSteps Component
 *
 * Manages approval/review steps with users/groups selection
 * Ported from lego/page/settings/workflow-templates/steps/UserGroupSteps.jsx
 */
export const UserGroupSteps: React.FC<UserGroupStepsProps> = ({
  steps,
  onChange,
  labelTxt,
  nofallTxt,
  disabled = false
}) => {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const [usersGroups, setUsersGroups] = useState<UserGroupOption[]>([])
  const [stepLabels, setStepLabels] = useState<StepLabelOption[]>([])

  useEffect(() => {
    usersApiService.getAssembledUsersGroups(false).then((data: any) => {
      const options = data.merged.map((user: any) => ({
        value: user._uid,
        label: user.chName,
        id: user.id,
        systemHelperType: user.systemHelperType
      }))
      setUsersGroups(options)
    })

    // Load step labels - getCrTeamTaskStepLabels requires lang parameter
    api.settings.getCrTeamTaskStepLabels('').then((data: any) => {
      const options = data.elements.map((label: any) => ({
        value: label.id,
        label: label.text
      }))
      setStepLabels(options)
    })
  }, [])

  useEffect(() => {
    // Ensure at least one step exists
    if (steps.length === 0) {
      addStep()
    }
  }, [])

  useEffect(() => {
    // Ensure all steps have unique IDs
    const stepsNeedIds = steps.some((step) => !step.id)
    if (stepsNeedIds) {
      const stepsWithIds = steps.map((step) => ({
        ...step,
        id: step.id || `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }))
      onChange(stepsWithIds)
    }
  }, [steps])

  const addStep = () => {
    const newStep: ApproverStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      processingType: PROCESSING_TYPES.SERIAL,
      taskTeamStepLabel: null,
      nofAllValue: null,
      usersGroups: []
    }
    onChange([...steps, newStep])
  }

  const removeStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index)
    onChange(newSteps)
  }

  const updateStep = (index: number, field: keyof ApproverStep, value: any) => {
    const newSteps = [...steps]
    newSteps[index] = { ...newSteps[index], [field]: value }
    onChange(newSteps)
  }

  return (
    <div className="form-group">
      <label className="control-label">{t(labelTxt)}</label>
      <div className="custom-approvers-table">
        {steps.length > 0 && (
          <div className="table-header">
            <span className="h steps ta-center">{t('STEP')}</span>
            <span className="h customlabel">{t('CR_CUSTOM_STEP_LABEL')}</span>
            <span className="h usersgroups">{t('USERS_GROUPS')}</span>
            <span className="h order">{t('ORDER')}</span>
          </div>
        )}

        {steps.map((step, index) => (
          <div key={step.id || index} className="table-row py5">
            <span className="h steps ta-center py5">{index + 1}</span>

            {/* Label Selector */}
            <span className="h customlabel">
              <Select
                id={`label-${index}`}
                name={`label-${index}`}
                placeholder={t('SELECT_CR_TASK_LABEL_PLACEHOLDER')}
                value={step.taskTeamStepLabel}
                options={stepLabels}
                onChange={(selected: any) => {
                  updateStep(index, 'taskTeamStepLabel', selected ? selected.value : null)
                }}
                isDisabled={disabled}
              />
            </span>

            {/* Users/Groups Multi-Select */}
            <span className="h usersgroups">
              <Select
                id={`usersgroups-${index}`}
                name={`usersgroups-${index}`}
                placeholder={t('SELECT_USERS/GROUPS...')}
                value={step.usersGroups}
                options={usersGroups}
                multi={true}
                onChange={(selected: any) => {
                  const values = selected ? selected.map((opt: any) => opt.value) : []
                  updateStep(index, 'usersGroups', values)
                }}
                isDisabled={disabled}
              />
            </span>

            {/* Processing Type */}
            <span className="h order">
              <select
                className="form-control"
                value={step.processingType}
                onChange={(e) => updateStep(index, 'processingType', e.target.value)}
                disabled={disabled}
              >
                <option value={PROCESSING_TYPES.SERIAL}>{t('ONE_AT_A_TIME')}</option>
                <option value={PROCESSING_TYPES.PARALLEL}>{t('ALL_AT_ONCE')}</option>
                <option value={PROCESSING_TYPES.N_OF_ALL}>{t('N_OF_ALL')}</option>
              </select>

              {step.processingType === PROCESSING_TYPES.N_OF_ALL && (
                <div className="nofall-wrapper hform-group" style={{ marginTop: '5px' }}>
                  <input
                    type="number"
                    className="md-input nofall-input"
                    style={{
                      width: '26px',
                      height: '25px',
                      lineHeight: '25px',
                      marginRight: '5px',
                      paddingLeft: '5px',
                      border: '1px solid #ccc',
                      borderRadius: '2px'
                    }}
                    value={step.nofAllValue ?? ''}
                    onChange={(e) => updateStep(index, 'nofAllValue', parseInt(e.target.value) || null)}
                    disabled={disabled}
                    min="1"
                  />
                  <span>{t(nofallTxt)}</span>
                </div>
              )}
            </span>

            {!disabled && (
              <button
                type="button"
                className="close"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                onClick={() => removeStep(index)}
                aria-label="Remove step"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {!disabled && (
        <button type="button" className="btn btn-default btn-sm" style={{ marginTop: '10px' }} onClick={addStep}>
          {t('ADD_STEP')}
        </button>
      )}
    </div>
  )
}
